import {InvoicePosType} from "../../../libs/enum/invoice-pos-type.enum";
import {InvoiceArticleDto} from "./invoice-article.dto";
import {InvoiceDiscountDto} from "./invoice-discount.dto";
import {isDefined, isNumeric} from "../../../libs/utils/epic.utils";
import {InvoiceExchangePositionDto} from "./invoice-exchange-position.dto";
import {AccAmountPipe} from "../../../libs/pipes/amount.pipe";

export class InvoicePositionDto implements AccAmountPipe {

  public id!: string;
  public type!: InvoicePosType;
  public pos!: number;
  public number: number | undefined;
  public quantity: number | undefined;
  public discount: InvoiceDiscountDto | undefined;
  public readonly text: string | undefined;
  public textTpl: string | undefined;
  public readonly netAmount: number | undefined;
  public readonly grossAmount: number | undefined;
  public readonly totalNetAmount: number | undefined;
  public readonly totalGrossAmount: number | undefined;
  public readonly totalVatAmount: number | undefined;
  public readonly totalNetAmountWithoutDiscount: number | undefined;
  public readonly totalGrossAmountWithoutDiscount: number | undefined;
  public readonly totalVatAmountWithoutDiscount: number | undefined;
  public article: InvoiceArticleDto | undefined;
  public readonly exchangePosition: InvoiceExchangePositionDto | undefined;
  // 'fractionalDigits' taken from InvoiceDto
  public readonly fractionalDigits!: number;
  // 'currencyCode' taken from InvoiceDto // fallback if no article
  public readonly currencyCode!: string;

  constructor(init?: Partial<InvoicePositionDto> | undefined) {
    Object.assign(this, init);
    if (init?.discount) {
      this.discount = new InvoiceDiscountDto(init.discount);
    }
    if (init?.article) {
      this.article = new InvoiceArticleDto(init.article);
    }
    if (init?.exchangePosition) {
      this.exchangePosition = new InvoiceExchangePositionDto(init.exchangePosition);
    }
  }

  // AccAmountPipe / used: CurrencyPipe / PaymentEnterComponent
  public getCurrencyCode(): string {
    return isDefined(this.article) ? this.article.currencyCode : this.currencyCode;
  }

  // AccAmountPipe / used: CurrencyPipe / PaymentEnterComponent
  public getPreferCurrencyCode(): string | undefined {
    if (this.hasExchangePos()) {
      return this.exchangePosition!.exchange.targetCurrencyCode;
    }
    return this.getCurrencyCode();
  }

  // AccAmountPipe / used: CurrencyPipe / PaymentEnterComponent
  public getFractionalDigits(): number {
    if (isNumeric(this.fractionalDigits)) {
      return this.fractionalDigits;
    }
    return 2; // Fallback
  }

  public hasDiscount(): boolean {
    return isDefined(this.discount) && isDefined(this.discount.type) && isDefined(this.discount.value);
  }

  public isArticle(): boolean {
    return this.type === InvoicePosType.ARTICLE && isDefined(this.article);
  }

  public hasExchangePos(): boolean {
    return isDefined(this.exchangePosition);
  }

  public getPreferSellingPriceNet(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.sellingPriceNet : this.article?.sellingPriceNet;
  }

  public getPreferNetAmount(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.netAmount : this.netAmount;
  }

  public getPreferGrossAmount(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.grossAmount : this.grossAmount;
  }

  public getPreferTotalNetAmountWithoutDiscount(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.totalNetAmountWithoutDiscount : this.totalNetAmountWithoutDiscount;
  }

  public getPreferTotalGrossAmountWithoutDiscount(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.totalGrossAmountWithoutDiscount : this.totalGrossAmountWithoutDiscount;
  }

  public getPreferDiscountValue(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.discountValue : this.discount?.value;
  }

  public getPreferDiscountNetAmount(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.discountNetAmount : this.discount?.netAmount;
  }

  public getPreferDiscountTotalNetAmount(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.discountTotalNetAmount : this.discount?.totalNetAmount;
  }

  public getPreferDiscountGrossAmount(): number | undefined {
    return this.hasExchangePos() ? this.exchangePosition!.discountGrossAmount : this.discount?.grossAmount;
  }
}
